import { ApolloClient, InMemoryCache, createHttpLink, split } from '@apollo/client/core';
import { setContext } from '@apollo/client/link/context';
import { onError } from '@apollo/client/link/error';
import { GraphQLWsLink } from '@apollo/client/link/subscriptions';
import { createClient } from 'graphql-ws';
import { getMainDefinition } from '@apollo/client/utilities';

// Variable global para almacenar el token dinámico para operaciones de pago
let currentPaymentAuthToken: string | null = null;

// Función para actualizar el token dinámicamente
export const setPaymentAuthToken = (token: string | null) => {
    currentPaymentAuthToken = token;
};

// Función para obtener el token actual
export const getPaymentAuthToken = (): string | null => {
    return currentPaymentAuthToken;
};

// URL del BFF de pago desde variable de entorno
let paymentGraphqlUrl = import.meta.env.VITE_GRAPHQL_PAYMENT;

if (!paymentGraphqlUrl) {
    throw new Error('❌ VITE_GRAPHQL_PAYMENT no está configurado en las variables de entorno');
}

// Asegurar que tenga protocolo https://
if (!paymentGraphqlUrl.startsWith('http://') && !paymentGraphqlUrl.startsWith('https://')) {
    paymentGraphqlUrl = 'https://' + paymentGraphqlUrl;
}

// Siempre agregar /query si no lo tiene
if (!paymentGraphqlUrl.endsWith('/query')) {
    paymentGraphqlUrl = paymentGraphqlUrl.replace(/\/$/, '') + '/query';
}

// Enlace HTTP para operaciones de pago
const paymentHttpLink = createHttpLink({
    uri: paymentGraphqlUrl,
    headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
    },
    fetch: (uri, options) => {
        return fetch(uri, options).then(response => {
            return response;
        }).catch(error => {
            console.error('❌ [apollo-payment] Fetch error:', error);
            throw error;
        });
    },
});

// Enlace de contexto para añadir el token JWT en los headers
const paymentAuthLink = setContext(async (_, { headers }) => {
    let token = currentPaymentAuthToken;

    // Si aún no hay token, esperar a que el frontend padre lo envíe (evita race condition 401)
    if (!token && typeof window !== 'undefined' && (window as any).microfrontendAuth?.waitForToken) {
        try {
            token = await (window as any).microfrontendAuth.waitForToken(5000);
            if (token) currentPaymentAuthToken = token;
        } catch {
            // Timeout: proceder sin token
        }
    }

    return {
        headers: {
            ...headers,
            'Content-Type': 'application/json',
            'Accept': 'application/json',
            authorization: token ? `Bearer ${token}` : '',
        }
    };
});

// Enlace de manejo de errores
const paymentErrorLink = onError(({ graphQLErrors, networkError }) => {
    if (graphQLErrors) {
        graphQLErrors.forEach(({ message, locations, path }) => {
            console.error(
                `[GraphQL Payment Error]: Message: ${message}, Location: ${locations}, Path: ${path}`
            );
            
            // Detectar errores de autenticación
            if (
                message?.toLowerCase().includes('unauthorized') ||
                message?.toLowerCase().includes('authentication') ||
                message?.toLowerCase().includes('jwt')
            ) {
                const hadToken = !!currentPaymentAuthToken;
                console.error('❌ [Apollo Payment] Token expirado o inválido.');
                currentPaymentAuthToken = null;
                
                // Solo notificar al padre si teníamos un token válido (no por race condition)
                if (hadToken && typeof window !== 'undefined' && (window as any).microfrontendAuth) {
                    (window as any).microfrontendAuth.notifyTokenExpired();
                }
            }
        });
    }

    if (networkError) {
        const statusCode = (networkError as any)?.statusCode;
        if (statusCode === 401 || statusCode === 403) {
            const hadToken = !!currentPaymentAuthToken;
            console.error('❌ [Apollo Payment] Error de autenticación (HTTP', statusCode, ')');
            currentPaymentAuthToken = null;
            
            // Solo notificar al padre si teníamos un token válido (no por race condition)
            if (hadToken && typeof window !== 'undefined' && (window as any).microfrontendAuth) {
                (window as any).microfrontendAuth.notifyTokenExpired();
            }
        }
    }
});

// Crear URL de WebSocket para suscripciones (reemplazar https:// con wss://)
const paymentWsUrl = paymentGraphqlUrl.replace(/^https:\/\//, 'wss://').replace(/^http:\/\//, 'ws://');

console.log('🔌 [apollo-payment] HTTP URL:', paymentGraphqlUrl);
console.log('🔌 [apollo-payment] WebSocket URL:', paymentWsUrl);

// Cliente WebSocket para suscripciones
const wsClient = createClient({
    url: paymentWsUrl,
    connectionParams: () => {
        const token = currentPaymentAuthToken;
        return {
            authorization: token ? `Bearer ${token}` : '',
        };
    },
    shouldRetry: () => true,
    retryAttempts: 3,
});

// Link de WebSocket para suscripciones
const paymentWsLink = new GraphQLWsLink(wsClient);

// Usar split para dirigir operaciones:
// - Subscriptions → WebSocket
// - Queries/Mutations → HTTP
const splitLink = split(
    ({ query }) => {
        const definition = getMainDefinition(query);
        return (
            definition.kind === 'OperationDefinition' &&
            definition.operation === 'subscription'
        );
    },
    paymentWsLink, // Suscripciones a WebSocket
    paymentErrorLink.concat(paymentAuthLink.concat(paymentHttpLink)) // Queries/Mutations a HTTP
);

// Cliente Apollo para operaciones de pago
const paymentApolloClient = new ApolloClient({
    link: splitLink,
    cache: new InMemoryCache(),
    defaultOptions: {
        watchQuery: {
            fetchPolicy: 'network-only',
            errorPolicy: 'all',
        },
        query: {
            fetchPolicy: 'network-only',
            errorPolicy: 'all',
        },
        mutate: {
            errorPolicy: 'all',
        },
    },
});

export default paymentApolloClient;
